/**
 * Personal-space predicates over ASSIGNMENT rows — the client mirror of
 * `server/helpers/personalSpace.js`, added 2026-08-11.
 *
 * ⚠️ NOT `utils/spaceLabel.js`, and the difference is the SHAPE, not the topic.
 * That module answers *"what do I call this thing on screen"* and takes a
 * PRODUCTION (`{ is_personal, owner_user_id, production_name, … }`). These
 * answer *"is this one of the caller's real workplaces"* and take an
 * ASSIGNMENT row from `GET /api/roles/my-assignments`
 * (`{ department_id, production_is_personal, … }`). Folding a routing decision
 * into a labelling module would give a display helper authority over where a
 * user lands.
 *
 * 🪤 THEY LIVE HERE RATHER THAN IN `DepartmentContext.js` FOR A CONCRETE
 * REASON, FOUND BY BREAKING IT: several component tests replace the whole
 * context module with `jest.mock('../contexts/DepartmentContext', () => ({
 * useDepartment: … }))`, so ANY second named export a component imports from
 * there is `undefined` at run time and the component throws — a failure that
 * reads as a broken component and is a broken mock.
 * `ProductionDepartmentSwitcher.deptNameCollision.test.js` went red exactly
 * that way. A plain util nobody mocks has no such trap, and both the context
 * and the switcher now import the ONE definition from here.
 */

/**
 * Is this assignment's production a REAL one, i.e. not somebody's My Space?
 *
 * 🔑 THE PREDICATE IS `production_is_personal`, AND THE COLUMN CHOICE IS THE
 * WHOLE OF IT. Never match on the name "My Space" — department and production
 * names are explicitly allowed to duplicate (`docs/ROLES.md` §A.1), so a name
 * is not an identity here. And never use `department_is_personal`: the two
 * flags genuinely DISAGREE on 10 rows of the dev database (measured
 * 2026-08-11), all of them real-looking departments sitting under a personal
 * production. `productions.is_personal` is the truth — it is the row that
 * carries `owner_user_id`, and it is what `server/helpers/personalSpace.js`
 * keys off. Both fields arrive on every assignment from
 * `GET /api/roles/my-assignments`.
 */
export const isRealAssignment = (a) => !!a && !a.production_is_personal;

/** Does the caller hold anywhere real to be? */
export const hasRealAssignment = (assignments) =>
  Array.isArray(assignments) && assignments.some(isRealAssignment);

/**
 * Should choosing `target` from the switcher raise the leaving-your-department
 * warning?
 *
 * Product owner, 2026-08-11: *"if the user chooses Myspace give them a pop up
 * warnign to tell them they are leaving their department and how to get back."*
 *
 * TRUE for exactly one transition — INTO a personal space, by someone who has
 * somewhere real to be, from somewhere else. Every other case is deliberately
 * silent, and each exclusion is its own reason rather than one rule:
 *
 *   - **personal-only users** — they are not leaving anything, and a warning
 *     about departments they do not have is pure noise aimed at precisely the
 *     users with the least context to make sense of it;
 *   - **real → real** — an ordinary department switch, the control's whole job;
 *   - **out of a personal space** — that is the RECOVERY the dialog tells them
 *     about, and warning on it would make the way back feel like a mistake;
 *   - **already there** — re-picking the active option leaves nothing.
 *
 * Pure and exported so the no-warn cases can be pinned directly: through a
 * rendered dropdown, an absent dialog is indistinguishable from a dialog that
 * failed to open for an unrelated reason.
 */
export function shouldWarnOnSwitch(assignments, target, activeDepartment) {
  if (!target || isRealAssignment(target)) return false;
  if (activeDepartment && activeDepartment.department_id === target.department_id) return false;
  return hasRealAssignment(assignments);
}

/**
 * Is `departmentId` a department of the caller's own personal (My Space)
 * workspace?
 *
 * Added 2026-09-11 for the "Header details" box, which renders in a My Space
 * context and nowhere else (owner decision 2). It is a SEPARATE question from
 * `isRealAssignment` above — that one takes an assignment ROW and answers "is
 * this somewhere real"; this takes an ID and has to go and find the row,
 * because the two callers hold an id and not a row:
 *
 *   • CREATE — the box is about the department the order will land in, which
 *     on a new order is the ACTIVE one.
 *   • EDIT — it is about the ORDER'S OWN department, which can differ from the
 *     active one: a caller can be standing anywhere while editing. The order
 *     detail payload carries `department_id`, so this is the only lookup that
 *     answers the right question.
 *
 * 🔑 SAME PREDICATE, SAME COLUMN. It delegates to `isRealAssignment`, so there
 * is exactly one place in the client that knows the answer is
 * `production_is_personal` — never `department_is_personal` (they disagree on
 * 10 measured dev rows) and never a name match ("My Space" is not unique).
 * Re-inlining the flag read here would be the second definition this module
 * exists to prevent.
 *
 * 🔒 FAILS CLOSED on everything it cannot resolve: a null/zero/non-positive id,
 * an id the caller holds no assignment for, an unloaded `assignments` array. A
 * department the caller is not in cannot be their personal space, and showing
 * the box while the context is still loading would flash a control that then
 * vanishes — and, worse, could leave a value in form state for an order that
 * turns out to be a department one.
 *
 * ⚠️ A UI CONVENIENCE, NEVER A GATE. The server re-decides independently from
 * `productions.is_personal` and stores NULL for a department order whatever the
 * client sent (`POST`/`PUT /api/orders`) — this only decides whether to offer
 * the control.
 */
export function isPersonalWorkspaceDepartment(assignments, departmentId) {
  const id = Number(departmentId);
  // 🪤 `Number(null)` is `0`, so the `> 0` test is load-bearing — the same trap
  // `capabilitiesFor` records in `DepartmentContext.js`.
  if (!Number.isInteger(id) || id <= 0) return false;
  if (!Array.isArray(assignments)) return false;
  const match = assignments.find((a) => Number(a && a.department_id) === id);
  return !!match && !isRealAssignment(match);
}
